import { ITEM_TYPES, microToDecimal, type Condition, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { api } from '../api';
import { TYPE_LABEL } from '../lib/labels';
import { blankLine, blankOrder, buildOrderBody, linesTotalMicro, usesColor, type OrderDraft, type OrderLineDraft } from '../lib/purchaseOrderForm';
import { useColors, useSettings } from '../queries';
import { Button, ErrorMessage, Field, Input, Notice, Select, Spinner, Textarea, money } from '../ui';

/**
 * A purchase order for something that did not come from BrickLink, such as an eBay order. It records what is on its
 * way and changes no stock: the goods become stock when they are received against the order.
 */
export function NewOrderForm({ onCreated }: { onCreated: (purchaseId: number) => void }) {
  const qc = useQueryClient();
  const colors = useColors();
  const settings = useSettings();
  if (colors.isPending || settings.isPending) return <Spinner />;
  const base = settings.data?.defaultCurrency ?? 'GBP';
  return <Form base={base} colors={colors.data ?? []} onCreated={onCreated} qc={qc} />;
}

function Form({ base, colors, onCreated, qc }: { base: string; colors: NonNullable<ReturnType<typeof useColors>['data']>; onCreated: (id: number) => void; qc: ReturnType<typeof useQueryClient> }) {
  const [d, setD] = useState<OrderDraft>(() => blankOrder(base));
  const [problems, setProblems] = useState<string[]>([]);
  const set = <K extends keyof OrderDraft>(k: K, v: OrderDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setLine = (key: number, patch: Partial<OrderLineDraft>) => setD((x) => ({ ...x, lines: x.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const foreign = d.currency.trim().toUpperCase() !== base.toUpperCase();
  const total = useMemo(() => linesTotalMicro(d.lines), [d.lines]);

  const create = useMutation({
    mutationFn: (body: unknown) => api<{ purchase: { id: number } }>('/api/v1/purchase-orders', { body }),
    onSuccess: ({ purchase }) => {
      for (const k of ['purchases', 'dashboard', 'items']) void qc.invalidateQueries({ queryKey: [k] });
      onCreated(purchase.id);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const built = buildOrderBody(d, colors, base);
    if (!built.ok) {
      setProblems(built.problems);
      return;
    }
    setProblems([]);
    create.mutate(built.body);
  }

  const chargeField = (label: string, k: 'shippingCost' | 'insuranceCost' | 'otherCharges' | 'taxAmount' | 'discountAmount') => (
    <Field label={label}>
      <Input inputMode="decimal" placeholder="0.00" value={d[k]} onChange={(e) => set(k, e.target.value)} />
    </Field>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Date ordered">
          <Input type="date" required value={d.purchasedOn} onChange={(e) => set('purchasedOn', e.target.value)} />
        </Field>
        <Field label="Seller">
          <Input value={d.sellerName} onChange={(e) => set('sellerName', e.target.value)} placeholder="e.g. brick_barn on eBay" />
        </Field>
        <Field label="Description" className="lg:col-span-2" hint="An order number or a reminder of what it was.">
          <Input value={d.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. eBay order 12-3456" />
        </Field>
        <Field label="Currency" hint={`Stock is costed in ${base}.`}>
          <Input maxLength={3} className="uppercase" value={d.currency} onChange={(e) => set('currency', e.target.value)} />
        </Field>
        {foreign && (
          <Field label={`Exchange rate to ${base}`} hint={`${base} for one ${d.currency.toUpperCase() || 'unit'}. Needed before the order can be received.`}>
            <Input inputMode="decimal" placeholder="0.85" value={d.exchangeRate} onChange={(e) => set('exchangeRate', e.target.value)} />
          </Field>
        )}
        <Field label="VAT scheme" hint="With the standard rate the VAT below is claimed back, so it is not costed into the stock.">
          <Select value={d.vatScheme} onChange={(e) => set('vatScheme', e.target.value as OrderDraft['vatScheme'])}>
            <option value="none">Not VAT-registered</option>
            <option value="standard">Standard rate</option>
            <option value="margin">Margin scheme</option>
          </Select>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {chargeField('Postage', 'shippingCost')}
        {chargeField('Insurance', 'insuranceCost')}
        {chargeField('Other charges', 'otherCharges')}
        {chargeField('VAT', 'taxAmount')}
        {chargeField('Discount', 'discountAmount')}
      </div>

      <datalist id="order-color-names">
        {colors
          .filter((c) => c.id !== 0)
          .map((c) => (
            <option key={c.id} value={c.name} />
          ))}
      </datalist>
      <div className="overflow-x-auto">
        <table className="data w-full min-w-[52rem] text-sm">
          <thead>
            <tr>
              <th>Type</th>
              <th>Item number</th>
              <th>Colour</th>
              <th>Condition</th>
              <th className="text-right">Quantity</th>
              <th className="text-right">Price each ({d.currency.toUpperCase() || base})</th>
              <th>Name, if new</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l) => (
              <tr key={l.key}>
                <td>
                  <Select aria-label="Item type" className="w-28" value={l.itemType} onChange={(e) => setLine(l.key, { itemType: e.target.value as ItemType })}>
                    {ITEM_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>
                  <Input aria-label="Item number" className="w-28" autoComplete="off" value={l.itemNo} onChange={(e) => setLine(l.key, { itemNo: e.target.value })} />
                </td>
                <td>
                  <Input
                    aria-label="Colour"
                    className="w-40"
                    list="order-color-names"
                    disabled={!usesColor(l.itemType)}
                    placeholder={usesColor(l.itemType) ? 'Type a colour' : 'not applicable'}
                    value={usesColor(l.itemType) ? l.colorText : ''}
                    onChange={(e) => setLine(l.key, { colorText: e.target.value })}
                  />
                </td>
                <td>
                  <Select aria-label="Condition" className="w-24 min-w-24" value={l.condition} onChange={(e) => setLine(l.key, { condition: e.target.value as Condition })}>
                    <option value="N">New</option>
                    <option value="U">Used</option>
                  </Select>
                </td>
                <td>
                  <Input aria-label="Quantity" inputMode="numeric" className="w-20 text-right" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} />
                </td>
                <td>
                  <Input aria-label="Price of one piece" inputMode="decimal" className="w-24 text-right" placeholder="0.05" value={l.unitPrice} onChange={(e) => setLine(l.key, { unitPrice: e.target.value })} />
                </td>
                <td>
                  <Input aria-label="Name if the item is new" className="w-44" value={l.itemName} onChange={(e) => setLine(l.key, { itemName: e.target.value })} />
                </td>
                <td>
                  <Button variant="ghost" small disabled={d.lines.length === 1} onClick={() => setD((x) => ({ ...x, lines: x.lines.filter((r) => r.key !== l.key) }))}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => setD((x) => ({ ...x, lines: [...x.lines, blankLine(x.lines.at(-1)?.condition)] }))}>
          Add a line
        </Button>
        <span className="text-sm text-slate-600">
          Items {money(microToDecimal(total), d.currency.toUpperCase() || base)}. Postage and charges are added when the order is saved.
        </span>
      </div>

      <Field label="Notes">
        <Textarea rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>

      {problems.length > 0 && (
        <Notice tone="amber">
          <ul className="list-disc pl-5">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Notice>
      )}
      <ErrorMessage error={create.error} />
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Saving…' : 'Create purchase order'}
      </Button>
    </form>
  );
}
